/**
 * Renewal Initiatives — App Registry
 * ----------------------------------
 * ONE source of truth for the suite. Every place an app is referenced
 * — the launcher, the top-bar switcher, a cross-app link, a breadcrumb —
 * pulls its icon + accent color from here, so the pairing cascades
 * consistently across all tools.
 *
 * Icons are from `lucide-react`.  ⚠ Lucide is a reviewed substitution —
 * the brand owner did not supply a first-party icon set.
 *
 *   npm i lucide-react
 */
import {
  LayoutGrid,
  Landmark,
  Banknote,
  Clock,
  SquarePen,
  SquareKanban,
  Wrench,
  Building2,
  Compass,
  type LucideIcon,
} from "lucide-react";

export type AppId =
  | "portal"
  | "finance"
  | "expenses"
  | "timesheets"
  | "proposals"
  | "tasks"
  | "equipment"
  | "htc"
  | "traction";

export interface AppMeta {
  id: AppId;
  /** Short product name shown in chrome ("Finance", not "financial-system"). */
  name: string;
  /** One-line description for launcher tiles. */
  blurb: string;
  /** Production URL (used by launcher tiles + cross-app links). */
  url: string;
  /** The app's signature Lucide icon. */
  icon: LucideIcon;
  /** Accent/chrome color (also exposed as --ri-app-<id> in theme.css). */
  accent: string;
}

export const RENEWAL_APPS: AppMeta[] = [
  {
    id: "portal",
    name: "Portal",
    blurb: "The front door — sign-on & people data",
    url: "https://tools.renewalinitiatives.org",
    icon: LayoutGrid,
    accent: "var(--ri-app-portal)",
  },
  {
    id: "finance",
    name: "Finance",
    blurb: "Fund ledger & compliance",
    url: "https://finance.renewalinitiatives.org",
    icon: Landmark,
    accent: "var(--ri-app-finance)",
  },
  {
    id: "expenses",
    name: "Expenses",
    blurb: "Receipts & reimbursement",
    url: "https://expenses.renewalinitiatives.org",
    icon: Banknote, // money-related, per brand decision
    accent: "var(--ri-app-expenses)",
  },
  {
    id: "timesheets",
    name: "Timesheets",
    blurb: "Compliant time tracking",
    url: "https://timesheets.renewalinitiatives.org",
    icon: Clock,
    accent: "var(--ri-app-timesheets)",
  },
  {
    id: "proposals",
    name: "Proposals",
    blurb: "Grant-writing workbench",
    url: "https://proposals.renewalinitiatives.org",
    icon: SquarePen,
    accent: "var(--ri-app-proposals)",
  },
  {
    id: "tasks",
    name: "Tasks",
    blurb: "Project boards",
    url: "https://tasks.renewalinitiatives.org",
    icon: SquareKanban,
    accent: "var(--ri-app-tasks)",
  },
  {
    id: "equipment",
    name: "Equipment",
    blurb: "Asset & maintenance tracker",
    url: "https://maintenance.renewalinitiatives.org",
    icon: Wrench,
    accent: "var(--ri-app-equipment)",
  },
  {
    id: "htc",
    name: "Tax Credits",
    blurb: "Historic tax-credit dashboard",
    url: "https://htc.renewalinitiatives.org",
    icon: Building2,
    accent: "var(--ri-app-htc)",
  },
  {
    id: "traction",
    name: "Traction",
    blurb: "Rocks, Scorecard & L10s",
    url: "https://traction.renewalinitiatives.org",
    icon: Compass,
    accent: "var(--ri-app-traction)",
  },
];

export const APP_BY_ID: Record<AppId, AppMeta> = Object.fromEntries(
  RENEWAL_APPS.map((a) => [a.id, a]),
) as Record<AppId, AppMeta>;

export function getApp(id: AppId): AppMeta {
  return APP_BY_ID[id];
}
